'use client'

import { useMemo, useState, type ReactNode } from 'react'
import dynamic from 'next/dynamic'
import LazyMount from '@/components/LazyMount'
import type { ProjetSurCarte } from '@/components/FranceMap'
import {
  COULEURS_FAMILLE, FAMILLES_CARTE, LIBELLES_FAMILLE, familleProjet,
  type FamilleCarte, type Locale,
} from '@/lib/site/accueil'
import { textesAccueil } from './accueil-textes'

/**
 * Carte des projets de l'accueil : la carte interactive du site (FranceMap,
 * Leaflet) en version allégée — points colorés par famille, sans
 * regroupement, sans zoom à la molette — avec les filtres Tous / Forêt /
 * Agriculture / Haies et vergers et la légende des comptes par famille.
 *
 * Premier affichage : l'image statique /api/carte-accueil (SVG construit sur
 * le même catalogue). Leaflet n'est chargé qu'à l'approche de la carte au
 * défilement (LazyMount + import dynamique) : rien dans le premier écran.
 * Le cadre a une hauteur fixe : aucun décalage de mise en page à l'arrivée
 * de la carte.
 */
const FranceMap = dynamic(() => import('@/components/FranceMap'), {
  ssr: false,
  loading: () => null,
})

const FILTRES: ('tous' | Exclude<FamilleCarte, 'batiment'>)[] = ['tous', 'foret', 'agriculture', 'haies-vergers']
const couleurPoint = (type: string) => COULEURS_FAMILLE[familleProjet(type)]
const CADRE = 'relative w-full h-[420px] sm:h-[480px] lg:h-[560px] rounded-2xl overflow-hidden bg-[#F4F6F2]'

function ImageStatique({ alt = '' }: { alt?: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element -- SVG servi par /api/carte-accueil, déjà léger
    <img src="/api/carte-accueil" alt={alt} className="absolute inset-0 w-full h-full object-contain p-3" loading="lazy" decoding="async" />
  )
}

export default function CarteAccueil({
  locale, points, comptes, children,
}: {
  locale: Locale
  points: ProjetSurCarte[]
  comptes: Record<FamilleCarte, number>
  /** Colonne « À la une », rendue côté serveur. */
  children: ReactNode
}) {
  const t = textesAccueil(locale).carte
  const [filtre, setFiltre] = useState<(typeof FILTRES)[number]>('tous')
  const visibles = useMemo(
    () => (filtre === 'tous' ? points : points.filter((p) => familleProjet(p.type) === filtre)),
    [filtre, points],
  )

  return (
    <section className="pt-20 md:pt-28 pb-6 bg-cream" id="carte-projets" data-testid="accueil-carte">
      <div className="container-site">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-[660px]">
            <p className="text-[13px] font-bold uppercase tracking-[0.12em] text-forest-light mb-3.5">{t.surtitre}</p>
            <h2 className="font-heading font-extrabold text-[30px] md:text-[40px] leading-[1.12] tracking-[-0.01em] text-[#14281F]">{t.titre}</h2>
            <p className="mt-4 text-[#4A5A52] text-[17px] leading-relaxed">{t.texte}</p>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label={t.filtres}>
            {FILTRES.map((f) => {
              const actif = f === filtre
              return (
                <button
                  key={f}
                  type="button"
                  aria-pressed={actif}
                  onClick={() => setFiltre(f)}
                  data-testid={`filtre-${f}`}
                  className={`text-sm px-4 py-2 rounded-full transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest ${
                    actif ? 'bg-forest text-white font-semibold border-2 border-forest' : 'bg-white text-[#3B4A42] border border-[#CFD6D1] hover:border-forest'
                  }`}
                >
                  {f === 'tous' ? t.tous : LIBELLES_FAMILLE[f][locale]}
                </button>
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-8 mt-10 items-start">
          <div className="bg-white border border-[#E6E4DC] rounded-3xl p-3 sm:p-5 flex flex-col gap-4 min-w-0">
            <div className={CADRE}>
              {/* Image statique : visible tant que Leaflet n'a pas pris la place, au-dessus d'elle */}
              <ImageStatique alt={t.alt} />
              <div className="absolute inset-0 z-[1]">
                <LazyMount placeholderClassName="w-full h-full" rootMargin="300px">
                  <FranceMap projects={visibles} height="h-full" couleurPoint={couleurPoint} regrouper={false} molette={false} compteur={false} />
                </LazyMount>
              </div>
            </div>
            <ul className="flex flex-wrap gap-x-6 gap-y-2.5 text-sm text-[#3B4A42] px-1" data-testid="legende-carte">
              {FAMILLES_CARTE.filter((f) => comptes[f] > 0).map((f) => (
                <li key={f} className="flex items-center gap-2" data-famille={f}>
                  <span className="w-3 h-3 rounded-full inline-block flex-shrink-0" style={{ background: COULEURS_FAMILLE[f] }} aria-hidden="true" />
                  {LIBELLES_FAMILLE[f][locale]} · {new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fr-FR').format(comptes[f])}
                </li>
              ))}
            </ul>
            <p className="text-xs text-[#8A958F] px-1">{t.aide}</p>
          </div>
          {children}
        </div>
      </div>
    </section>
  )
}
